import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Skeleton, Empty, Tooltip, Modal, Input } from "antd";
import Swal from "sweetalert2";
import {
  DollarOutlined, PlusOutlined, ReloadOutlined, EditOutlined, DeleteOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { isSuperAdmin, isAdmin } from "../utils/role";
import TaperEditor from "./TaperEditor";
import { ratesForBand, rateText as rate } from "./taper";
import {
  useSegments, useCreateSegment, useTiers, useAllPlans, useDeleteTier,
} from "../hooks/useRateCard";

/**
 * Rate Card — the price list itself.
 *
 * One row per band, grouped into segment tabs. This is the only place prices are
 * authored: the proposal form never computes one, it asks /proposals/calculate,
 * which reads these rows. So an edit here is what reps quote on their next
 * keystroke.
 */

const card = {
  background: "white", borderRadius: 16, border: "1px solid #f0f0f0",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden",
};
const th = {
  padding: "13px 16px", textAlign: "left", fontSize: 11.5, fontWeight: 700, color: "#475569",
  textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap",
};
const td = { padding: "14px 16px", fontSize: 13, color: "#374151", verticalAlign: "middle" };
const numTd = { ...td, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };

const inr = (n) =>
  n == null || n === "" ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const num = (n) => (n == null || n === "" ? "—" : Number(n).toLocaleString("en-IN"));
const pct = (n) => (n == null || n === "" ? "—" : `${n}%`);

/** The band a tier covers, written the way a rep reads it. */
const bandLabel = (t) => {
  if (t.min_marks == null && t.max_marks == null) return "Any volume";
  if (t.max_marks == null) return `${num(t.min_marks)}+ marks`;
  return `${num(t.min_marks ?? 0)} – ${num(t.max_marks)} marks`;
};

const quoteLink = (color = "#475569", bg = "#fff", border = "#e5e7eb") => ({
  display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 16px",
  borderRadius: 10, fontSize: 13, fontWeight: 700, textDecoration: "none",
  border: `1px solid ${border}`, background: bg, color,
});


/** −1 is the stored way of saying "as many as they like". */
const isUnlimited = (v) => Number(v) === -1;

const clientsLabel = (t) =>
  t.free_clients == null ? "—" : isUnlimited(t.free_clients) ? "Unlimited" : num(t.free_clients);

const RateCard = () => {
  const mayEdit = isSuperAdmin() || isAdmin();

  const { data: segments = [], isLoading: segmentsLoading } = useSegments();
  const [segment, setSegment] = useState(null);
  const [addingSegment, setAddingSegment] = useState(false);
  const navigate = useNavigate();

  /* Land on the first tab as soon as there is one, and never sit on a tab that
     has since been removed. */
  useEffect(() => {
    if (!segments.length) return;
    if (!segment || !segments.some((s) => s.slug === segment)) setSegment(segments[0].slug);
  }, [segments, segment]);

  const { data: tiers = [], isLoading, isFetching, refetch } = useTiers(segment);
  const { data: plans = [] } = useAllPlans();
  const deleteTier = useDeleteTier();

  /* The row rides along so the editor opens filled in; the segment is in the URL
     so a refresh or a pasted link still knows which tab it came from. */
  const openTier = (tier) =>
    navigate(
      tier ? `/cms/rate-card/edit-tier/${tier.id}?segment=${segment}` : `/cms/rate-card/add-tier?segment=${segment}`,
      { state: tier ? { tier } : undefined },
    );

  const confirmDelete = (tier) => {
    Swal.fire({
      title: `Delete “${tier.name}”?`,
      text: "Reps will stop being able to quote this band. Quotes already sent keep their prices.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Delete tier",
    }).then((r) => { if (r.isConfirmed) deleteTier.mutate(tier.id); });
  };

  const current = segments.find((s) => s.slug === segment);

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* Header */}
          <div style={{ background: "linear-gradient(135deg,#1d4ed8 0%,#0f172a 100%)", borderRadius: 16, padding: "26px 30px", marginBottom: 20, boxShadow: "0 8px 32px rgba(29,78,216,0.25)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 54, height: 54, background: "rgba(255,255,255,.18)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, color: "white" }}>
                <DollarOutlined />
              </div>
              <div>
                <h4 style={{ color: "white", margin: 0, fontSize: 23, fontWeight: 700 }}>Rate Card</h4>
                <p style={{ color: "rgba(255,255,255,.8)", margin: 0, fontSize: 14 }}>
                  Every price a proposal can quote
                </p>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Tooltip title="Refresh">
                <button
                  onClick={() => refetch()}
                  style={{ width: 40, height: 40, borderRadius: 10, border: "1px solid rgba(255,255,255,0.22)", background: "rgba(255,255,255,0.1)", color: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}
                >
                  <ReloadOutlined spin={isFetching} />
                </button>
              </Tooltip>
              {mayEdit && (
                <button
                  onClick={() => openTier(null)}
                  disabled={!segment}
                  style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 40, padding: "0 18px", borderRadius: 10, border: "none", background: "white", color: "#1d4ed8", fontWeight: 700, fontSize: 13.5, cursor: segment ? "pointer" : "not-allowed", opacity: segment ? 1 : 0.6 }}
                >
                  <PlusOutlined /> Add tier
                </button>
              )}
            </div>
          </div>

          {/* Segment tabs, and the two things these prices are for */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20, alignItems: "center" }}>
            {segmentsLoading ? (
              <Skeleton.Button active style={{ width: 220, height: 38 }} />
            ) : (
              segments.map((s) => {
                const on = s.slug === segment;
                return (
                  <button
                    key={s.slug}
                    onClick={() => setSegment(s.slug)}
                    style={{
                      padding: "9px 20px", borderRadius: 10, fontSize: 13.5, fontWeight: 700,
                      cursor: "pointer",
                      border: `1px solid ${on ? "#1d4ed8" : "#e5e7eb"}`,
                      background: on ? "#1d4ed8" : "#fff",
                      color: on ? "#fff" : "#475569",
                      opacity: s.is_active === false ? 0.55 : 1,
                    }}
                  >
                    {s.label}
                  </button>
                );
              })
            )}
            {mayEdit && !segmentsLoading && (
              <button
                onClick={() => setAddingSegment(true)}
                style={{ padding: "9px 18px", borderRadius: 10, fontSize: 13.5, fontWeight: 700, cursor: "pointer", border: "1px dashed #cbd5e1", background: "#fff", color: "#64748b" }}
              >
                <PlusOutlined /> Add segment
              </button>
            )}

            {/* These prices exist to be quoted, so the two documents that quote
                them start here — already on the segment being looked at. */}
            <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Link
                to="/cms/proposals/create"
                state={{ payload: { segment } }}
                style={quoteLink("#5b48e8", "#f5f3ff", "#ddd6fe")}
              >
                <FileTextOutlined /> Create proposal
              </Link>
              <Link
                to="/cms/quotations/create"
                state={{ payload: { segment } }}
                style={quoteLink("#0d9488", "#f0fdfa", "#99f6e4")}
              >
                <FileTextOutlined /> Create quotation
              </Link>
            </div>
          </div>

          <TaperEditor segment={current} mayEdit={mayEdit} />

          {/* Tiers */}
          <div style={card}>
            {isLoading ? (
              <div style={{ padding: 24 }}><Skeleton active paragraph={{ rows: 5 }} /></div>
            ) : tiers.length === 0 ? (
              <div style={{ padding: "48px 20px" }}>
                <Empty description={`No tiers in ${current?.label || "this segment"} yet — a rep cannot quote it until one exists.`} />
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead style={{ background: "#fafbff" }}>
                    <tr>
                      <th style={th}>Tier</th>
                      <th style={th}>Band</th>
                      <th style={{ ...th, textAlign: "right" }}>Price</th>
                      <th style={{ ...th, textAlign: "right" }}>Onboarding</th>
                      <th style={{ ...th, textAlign: "right" }}>Users</th>
                      <th style={{ ...th, textAlign: "right" }}>Clients</th>
                      <th style={{ ...th, textAlign: "right" }}>Prepay Q / A</th>
                      <th style={{ ...th, textAlign: "right" }}>Max disc.</th>
                      <th style={th}>Plan</th>
                      {mayEdit && <th style={{ ...th, textAlign: "right" }}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {tiers.map((t) => {
                      const perMark = Array.isArray(current?.taper) && current.taper.length > 0;
                      const bandRates = perMark
                        ? ratesForBand(current.taper, t.min_marks, t.max_marks)
                        : [];
                      const plan = plans.find((p) => p.id === t.plan_id);
                      const off = t.is_active === false;
                      return (
                        <tr key={t.id} style={{ borderTop: "1px solid #f1f5f9", opacity: off ? 0.55 : 1 }}>
                          <td style={td}>
                            <div style={{ fontWeight: 700, color: "#111827" }}>{t.name}</div>
                            {off && (
                              <div style={{ fontSize: 11, color: "#9ca3af" }}>Inactive — not offered</div>
                            )}
                          </td>
                          <td style={{ ...td, whiteSpace: "nowrap", color: "#6b7280" }}>{bandLabel(t)}</td>
                          <td style={numTd}>
                            {/* Two shapes of price, each shown in its own terms
                                rather than flattened into one number. */}
                            {perMark ? (
                              <>
                                {/* The rates the taper applies across this band —
                                    two when the band straddles a step. */}
                                {bandRates.length > 0 ? (
                                  <div>
                                    {bandRates.length === 1
                                      ? rate(bandRates[0])
                                      : `${rate(bandRates[0])} – ${rate(bandRates[bandRates.length - 1])}`}
                                    <span style={{ color: "#9ca3af" }}> /mark/yr</span>
                                  </div>
                                ) : (
                                  <div style={{ color: "#9ca3af" }}>by taper</div>
                                )}
                                {t.floor_monthly != null && (
                                  <div style={{ fontSize: 11.5, color: "#9ca3af" }}>min {inr(t.floor_monthly)}/mo</div>
                                )}
                              </>
                            ) : (
                              <div>{inr(t.price_monthly)}<span style={{ color: "#9ca3af" }}> /mo</span></div>
                            )}
                          </td>
                          <td style={numTd}>{inr(t.onboarding_charge)}</td>
                          <td style={numTd}>
                            <div>{num(t.included_users)}</div>
                            {t.extra_user_cost != null && (
                              <div style={{ fontSize: 11.5, color: "#9ca3af" }}>+{inr(t.extra_user_cost)} ea</div>
                            )}
                          </td>
                          <td style={numTd}>
                            <div>{clientsLabel(t)}</div>
                            {/* Nothing is ever "extra" on an unlimited book, so the
                                per-client price has nothing to price. */}
                            {t.extra_client_cost && !isUnlimited(t.free_clients) ? (
                              <div style={{ fontSize: 11.5, color: "#9ca3af" }}>+{inr(t.extra_client_cost)} ea</div>
                            ) : null}
                          </td>
                          <td style={numTd}>{pct(t.quarterly_discount_pct)} / {pct(t.annual_discount_pct)}</td>
                          <td style={numTd}>
                            <div>{pct(t.max_additional_discount_pct)}</div>
                            <div style={{ fontSize: 11.5, color: "#9ca3af" }}>onb {pct(t.max_onboarding_discount_pct)}</div>
                          </td>
                          <td style={{ ...td, whiteSpace: "nowrap" }}>
                            {plan ? (
                              <span style={{ background: "#f5f3ff", color: "#5b48e8", border: "1px solid #ddd6fe", borderRadius: 20, padding: "2px 11px", fontSize: 11.5, fontWeight: 700 }}>
                                {plan.name || plan.plan_name}
                              </span>
                            ) : (
                              <span style={{ fontSize: 12, color: "#9ca3af" }}>Own features</span>
                            )}
                          </td>
                          {mayEdit && (
                            <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                              <div style={{ display: "inline-flex", gap: 6 }}>
                                <Tooltip title="Edit this tier">
                                  <button
                                    onClick={() => openTier(t)}
                                    style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid #bfdbfe", background: "#eff6ff", color: "#2563eb", cursor: "pointer" }}
                                  >
                                    <EditOutlined />
                                  </button>
                                </Tooltip>
                                <Tooltip title="Delete this tier">
                                  <button
                                    onClick={() => confirmDelete(t)}
                                    style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", cursor: "pointer" }}
                                  >
                                    <DeleteOutlined />
                                  </button>
                                </Tooltip>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {!mayEdit && (
            <div style={{ marginTop: 16, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 12, padding: "11px 16px", fontSize: 12.5, color: "#92400e" }}>
              You are viewing the rate card. Prices are edited by an admin.
            </div>
          )}

          <AddSegmentModal open={addingSegment} onClose={() => setAddingSegment(false)} />
        </div>
      </div>
    </div>
  );
};

/** A new segment is just a new tab — its tiers are added the same way as any. */
const AddSegmentModal = ({ open, onClose }) => {
  const create = useCreateSegment();
  const [label, setLabel] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) { setLabel(""); setSlug(""); setSlugTouched(false); setError(""); }
  }, [open]);

  /* The slug follows the label until someone edits it themselves — then it is
     theirs and we stop rewriting it under them. */
  const onLabel = (v) => {
    setLabel(v);
    if (!slugTouched) setSlug(v.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
  };

  const submit = () => {
    if (!label.trim()) return setError("Give the segment a name.");
    if (!slug.trim()) return setError("The slug is what the API calls this segment.");
    create.mutate({ slug: slug.trim(), label: label.trim() }, { onSuccess: onClose });
  };

  const fieldLabel = { fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 };

  return (
    <Modal
      open={open} onCancel={onClose} onOk={submit}
      title={<span style={{ fontWeight: 700 }}>Add a segment</span>}
      okText={create.isPending ? "Adding…" : "Add segment"}
      okButtonProps={{ loading: create.isPending }}
      destroyOnClose width={440}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 4 }}>
        <div>
          <label style={fieldLabel}>Name</label>
          <Input value={label} onChange={(e) => { onLabel(e.target.value); setError(""); }} placeholder="Agencies" />
        </div>
        <div>
          <label style={fieldLabel}>Slug</label>
          <Input value={slug} onChange={(e) => { setSlug(e.target.value); setSlugTouched(true); setError(""); }} placeholder="agencies" />
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 5 }}>
            Lowercase, no spaces. It goes in the URL, so it is awkward to change later.
          </div>
        </div>
        <div style={{ fontSize: 11.5, color: "#9ca3af", lineHeight: 1.6 }}>
          A new segment starts empty. Add its tiers before a rep can quote it.
        </div>
        {error && <div style={{ color: "#dc2626", fontSize: 12.5 }}>{error}</div>}
      </div>
    </Modal>
  );
};

export default RateCard;
